/**
 * The country page's URL state in one place: ?tab= (clean URL for board),
 * ?container= (editor drawer) + ?etab= (the drawer's initial tab — a
 * tutorial chapter opens the editor ON its tab), ?ch= (tutorial chapter),
 * ?part=admin (How it works › Admin guide — admins only, 21/10/2026),
 * ?at= (DEV simulated clock — the day the preview, Live tab and simulation
 * act on), ?group= (the campaign group the page is narrowed to; absent =
 * all; an older ?campaign=<wiserId> link opens that campaign's group).
 * `linkChose` = the link names its own view, so the remembered one waits.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useSearchParams } from 'react-router-dom';

import type { EditorTabKey } from '@/features/drop-generator/components/editor/EditorTabs';

import type { CountryTab } from './CountryTabs';

const TABS: CountryTab[] = ['board', 'timeline', 'live', 'gateways', 'errors', 'tutorial'];
/** Keys that make a link say what to show (besides the view's own key). */
const VIEW_KEYS = ['group', 'campaign', 'container', 'master', 'ch'];
const EDITOR_TABS: EditorTabKey[] = [
  'general',
  'groups',
  'gateways',
  'creative',
  'options',
  'verdicts',
  'autosend',
];
export const TUTORIAL_CHAPTERS = 10;

/** Merge entries into the current params; null deletes a key. */
function mergeParams(prev: URLSearchParams, entries: Record<string, string | null>) {
  const params = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(entries)) {
    if (value === null) params.delete(key);
    else params.set(key, value);
  }
  return params;
}

/** Tutorial chapter 1..10 from ?ch=, null when absent or out of range. */
function parseChapter(raw: string | null): number | null {
  const n = Number(raw);
  return raw && Number.isInteger(n) && n >= 1 && n <= TUTORIAL_CHAPTERS ? n : null;
}

/** A campaign wiserId from ?campaign=, null when absent or not a positive integer. */
function parseCampaign(raw: string | null): number | null {
  const n = Number(raw);
  return raw && Number.isInteger(n) && n > 0 ? n : null;
}

/** `tabKey` — the URL key of the view (Board / Day timeline / Live / How it
 *  works): "tab" on the page, another name when the page is embedded where
 *  "tab" is taken (the SMSWiser country page's Automation tab). */
export function useCountryPageParams({ tabKey = 'tab' }: { tabKey?: string } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(tabKey) as CountryTab | null;
  const etabParam = searchParams.get('etab') as EditorTabKey | null;
  const setParams = (entries: Record<string, string | null>) => {
    const { tab, ...rest } = entries;
    const mapped = tab === undefined ? rest : { ...rest, [tabKey]: tab };
    setSearchParams((prev) => mergeParams(prev, mapped), { replace: true });
  };
  return {
    tab: tabParam && TABS.includes(tabParam) ? tabParam : ('board' as CountryTab),
    containerParam: searchParams.get('container'),
    /** Phase 14 — a master's editor: its id, or 'new'. */
    masterParam: searchParams.get('master'),
    /** A new master config copies this one's sections (?master=new&copy=<id>). */
    copyParam: searchParams.get('copy'),
    /** The editor's initial tab when opened from a link; null = default. */
    editorTab: etabParam && EDITOR_TABS.includes(etabParam) ? etabParam : null,
    chapter: parseChapter(searchParams.get('ch')),
    /** How it works › Admin guide (the tab falls back to the tutorial without the permission). */
    adminPart: searchParams.get('part') === 'admin',
    /** The open campaign group's key; null = every group. */
    group: searchParams.get('group'),
    linkChose: [tabKey, ...VIEW_KEYS].some((key) => searchParams.has(key)),
    /** Legacy ?campaign=<wiserId> — opens that campaign's group. */
    campaign: parseCampaign(searchParams.get('campaign')),
    /** ISO instant or null = the real clock. DEV builds only — production
     *  can never render a non-today page from a pasted URL. */
    at: import.meta.env.DEV ? searchParams.get('at') : null,
    setParams,
  };
}
